import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isBefore, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useUserPlanStatusWithTransactionLimit } from '../../../hooks/useUserPlanStatusWithTransactionLimit';
import { MutationOptions } from '../../../services/general.types';
import { useTaxReportService } from '../../../services/tax-report.service';
import { useTransactionService } from '../../../services/transaction.service';
import { useUserService } from '../../../services/user.service';
import { TaxPersonalData } from '../../../services/user.types';

const getTaxReportFormat = (
    countryId: string,
    taxPersonalData: TaxPersonalData,
    isModelo721: boolean,
    endDateStr: string
): string => {
    const endDate = parseISO(endDateStr);

    if (countryId === 'Germany') {
        return taxPersonalData.kapsoFirstName ? 'GermanReportWithForms' : 'GermanReport';
    } else if (countryId === 'United States') {
        return 'Form8949Report';
    } else if (countryId === 'Austria') {
        return isBefore(endDate, parseISO('2022-03-02T00:00:00Z')) ? 'OldAustrianReport' : 'AustrianReport';
    } else if (countryId === 'United Kingdom') {
        return [2020, 2021, 2022, 2023, 2024].includes(endDate.getFullYear())
            ? 'UnitedKingdomReportWithForms'
            : 'UnitedKingdomReport';
    } else if (countryId === 'Canada') {
        return 'CanadianReport';
    } else if (countryId === 'Spain') {
        return isModelo721 ? 'SpanishModello721Report' : 'SpanishReport';
    } else if (countryId === 'New Zealand') {
        return 'NewZealandReport';
    } else if (countryId === 'Finland') {
        return 'FinnishReport';
    } else if (countryId === 'Denmark') {
        return 'DanishReport';
    } else if (countryId === 'Belgium') {
        return 'BelgianReport';
    } else if (countryId === 'India') {
        return 'IndianReport';
    } else if (countryId === 'Italy') {
        return isBefore(endDate, parseISO('2023-01-01T00:00:00Z')) ? 'ItalianReport' : 'Italian2023Report';
    } else if (countryId === 'Australia') {
        return taxPersonalData.format || 'AustralianReport';
    }

    return 'GeneralReport';
};

export const useTaxReportQueries = () => {
    const { currentCtUser } = useAuth();
    const queryClient = useQueryClient();
    const taxReportService = useTaxReportService();
    const transactionService = useTransactionService();
    const userService = useUserService();
    const { isLoadingUserPlans, isPurchased, hasEnoughTransactionLimit } = useUserPlanStatusWithTransactionLimit();
    const isModelo721 = false;

    const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['taxReports'] });
        queryClient.invalidateQueries({ queryKey: ['reportSummary'] });
        queryClient.invalidateQueries({ queryKey: ['reportDetailed'] });
    };

    const { data: taxPersonalData = null, isLoading: taxPersonalDataLoading } = useQuery({
        queryKey: ['taxPersonalData'],
        queryFn: async () => {
            return userService.getTaxPersonalData();
        },
        enabled: !!currentCtUser,
        gcTime: 0,
        staleTime: 0,
    });

    const { data: taxReports = [], isLoading: reportsLoading } = useQuery({
        queryKey: ['taxReports'],
        queryFn: async () => {
            return taxReportService.getAll();
        },
        enabled: !!currentCtUser,
        gcTime: 0,
        staleTime: 0,
    });

    const yearOptions = [...new Set(taxReports.map((report) => report.tax_year))].sort(
        (a, b) => parseInt(b) - parseInt(a)
    );
    useEffect(() => {
        if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
            setSelectedYear(yearOptions[0]);
        }
    }, [yearOptions, selectedYear]);

    const selectedReport = taxReports.find((report) => report.tax_year === selectedYear);
    const selectedReportId = selectedReport?.id;

    const canLoadData = !!selectedReportId && !!currentCtUser && !!selectedYear;

    const { data: transactionStats, isLoading: statsLoading } = useQuery({
        queryKey: ['transactionStats', selectedYear, selectedReportId],
        queryFn: async () => {
            return transactionService.getStats(selectedYear, selectedReportId!);
        },
        enabled: canLoadData,
        gcTime: 0,
        staleTime: 0,
    });

    const canLoadDetails: boolean =
        canLoadData &&
        !isLoadingUserPlans &&
        transactionStats !== undefined &&
        isPurchased(selectedYear) &&
        hasEnoughTransactionLimit(selectedYear, transactionStats.totalTransactions);

    const { data: reportSummary, isLoading: summaryLoading } = useQuery({
        queryKey: ['reportSummary', selectedReportId],
        queryFn: async () => {
            return taxReportService.reportsFullExportSummary(selectedReportId!);
        },
        enabled: canLoadDetails,
        gcTime: 0,
        staleTime: 0,
    });

    const { data: reportDetailed, isLoading: detailedLoading } = useQuery({
        queryKey: ['reportDetailed', selectedReportId],
        queryFn: async () => {
            return taxReportService.getTaxReportDetailed(selectedReportId!);
        },
        enabled: canLoadDetails,
        gcTime: 0,
        staleTime: 0,
    });

    const { mutate: baseDownloadPdfMutation, isPending: isDownloadingPdf } = useMutation({
        mutationFn: taxReportService.downloadPdf,
    });

    const { mutate: baseDownloadExcelMutation, isPending: isDownloadingExcel } = useMutation({
        mutationFn: taxReportService.downloadExcel,
    });

    const { mutate: baseDeleteTaxReport, isPending: isDeleting } = useMutation({
        mutationKey: ['deleteTaxReport'],
        mutationFn: taxReportService.delete,
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error deleting tax report:', error);
            invalidateQueries();
        },
    });

    const formatTaxPersonalData = (
        filename: string,
        groupTransactions: boolean | null = null,
        fullExtendedReport: boolean | null = null
    ) => {
        if (!selectedReport || !selectedReport.parameters || !currentCtUser || !taxPersonalData || !canLoadDetails)
            return null;

        let isSummary: boolean | null;
        let isFull: boolean | null;
        if (groupTransactions === true) {
            isFull = true;
            isSummary = false;
        } else if (groupTransactions === false) {
            isFull = false;
            isSummary = false;
        } else {
            isFull = null;
            isSummary = null;
        }

        return {
            filename: filename,
            id: selectedReportId,
            user_id: currentCtUser.ctId,
            personal_data: taxPersonalData.taxDataText,
            format: getTaxReportFormat(
                selectedReport.parameters.country,
                taxPersonalData,
                isModelo721,
                selectedReport.parameters.end_date
            ),
            export_settings: {
                capital_gains_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                capital_gains_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                donation_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                donation_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                income_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                income_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                fee_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                fee_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                lost_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                lost_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                capital_assets_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                capital_assets_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                closing_report_summary: isSummary !== null ? isSummary : taxPersonalData.groupTransactions,
                closing_report_full: isFull !== null ? isFull : !taxPersonalData.groupTransactions,
                fiat_decimal_digits: taxPersonalData.showAllDigits ? 8 : 2,
                show_trades: fullExtendedReport !== null ? fullExtendedReport : taxPersonalData.fullExtendReport,
                form8949: {
                    name: taxPersonalData.form8949Name ? taxPersonalData.form8949Name : '',
                    ssn: taxPersonalData.form8949Snn ? taxPersonalData.form8949Snn : '',
                    layout: taxPersonalData.form8949Layout,
                    round: taxPersonalData.form8949Round,
                    skip_zero_gain: taxPersonalData.form8949SkipZeroGain,
                    skip_empty_pages: taxPersonalData.form8949SkipEmptyPages,
                    attachments: taxPersonalData.form8949Attachments,
                },
                kap_plus_so: {
                    first_name: taxPersonalData.kapsoEnabled ? taxPersonalData.kapsoFirstName : '',
                    last_name: taxPersonalData.kapsoEnabled ? taxPersonalData.kapsoLastName : '',
                    tax_number: taxPersonalData.kapsoEnabled ? taxPersonalData.kapsoTin : '',
                },
            },
        };
    };

    const downloadPdf = (filename: string, options?: MutationOptions) => {
        if (!selectedReportId) return;
        if (!taxPersonalData) return;
        if (!canLoadDetails) return;
        baseDownloadPdfMutation(formatTaxPersonalData(filename), {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const downloadExcel = (filename: string, options?: MutationOptions) => {
        if (!selectedReportId) return;
        if (!taxPersonalData) return;
        if (!canLoadDetails) return;
        baseDownloadExcelMutation(formatTaxPersonalData(filename), {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const deleteTaxReport = (options?: MutationOptions) => {
        if (!selectedReportId) return;
        if (!canLoadDetails) return;
        baseDeleteTaxReport(selectedReportId, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    return {
        taxReports,
        selectedYear,
        setSelectedYear,
        taxPersonalData,
        canLoadDetails,
        reportSummary,
        reportDetailed,
        transactionStats,
        yearOptions,
        formatTaxPersonalData,
        downloadPdf,
        downloadExcel,
        deleteTaxReport,
        isLoading:
            reportsLoading ||
            taxPersonalDataLoading ||
            summaryLoading ||
            detailedLoading ||
            statsLoading ||
            isLoadingUserPlans,
        isDownloadingPdf,
        isDownloadingExcel,
        isDeleting,
    };
};
